// buildTripData — pure assembly of the trip object posted to the API.
//
// Extracted VERBATIM from `components/ManualTripCreator.js` (the `buildTripData`
// helper) as part of the behavior-preserving refactor (issue #240). It assembles
// the exact trip object the container hands to
// `enhanceTripWithImmediateFlightStatus` and then POSTs to `/trips`. All field
// names, the start/end epoch conversions, the per-flight timestamp conversion,
// the empty `hotels` / `carRentals` / `meetings` arrays, the `hasCheckedBaggage:
// false` / `groundTransport: undefined` defaults, the `userId` stamping, the
// generated `tripId`, and the `source: 'manual'` marker are preserved exactly.
//
// CLOCK / RNG / ID INJECTION:
//   The source read the wall-clock once (`const now = Date.now()`) and used that
//   single value for `tripId`, `createdAt`, and `updatedAt`, and read
//   `Math.random()` inline for the `tripId` suffix. To keep this function pure
//   and deterministic, the wall-clock is injected as `now` (defaulting to
//   `Date.now()`) and the id construction is injected as `generateId`
//   (defaulting to the real `generateTripId`, which itself defaults its random
//   seed to `Math.random()`). `generateId` is called as `generateId(now)` so the
//   id's timestamp component matches `createdAt` / `updatedAt`, exactly as the
//   source's shared `now` did. With both injected the result is fully
//   deterministic; with the defaults it reproduces the source's runtime output.
//
// Totality mirrors the source EXACTLY:
//   - `formData.startDate + 'T12:00:00'` and `formData.endDate + 'T12:00:00'`
//     are parsed via `new Date(...).getTime()`, copied verbatim. The source did
//     NOT guard `formData` or its fields here (they are validated upstream by
//     `validateForm`), so NO extra guard is added.
//   - `formData.tripName.trim()` / `startDestination.trim()` /
//     `endDestination.trim()` are identical to the source.
//   - Per-flight timestamp conversion is delegated to `normalizeFlightTimestamps`
//     (extracted verbatim from the same source block).
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import generateTripId from './generateTripId.js';
import normalizeFlightTimestamps from './normalizeFlightTimestamps.js';

/**
 * Assemble the trip object from validated form data, the resolved user id, and
 * the added flights.
 *
 * @param {Object} formData - The validated form values.
 * @param {string} userId - The resolved authenticated user id.
 * @param {Array<Object>} flights - The flights added to the trip.
 * @param {number} [now=Date.now()] - The wall-clock timestamp (epoch ms) used
 *                 for `tripId`, `createdAt`, and `updatedAt`.
 * @param {(now: number) => string} [generateId=generateTripId] - The trip-id
 *                 factory (injected for determinism; defaults to the real one).
 * @returns {Object} The assembled trip object.
 */
export function buildTripData(formData, userId, flights, now = Date.now(), generateId = generateTripId) {
  const startDateTime = new Date(formData.startDate + 'T12:00:00').getTime();
  const endDateTime = new Date(formData.endDate + 'T12:00:00').getTime();

  const flightsWithTimestamps = normalizeFlightTimestamps(flights);

  return {
    tripId: generateId(now),
    userId,
    tripName: formData.tripName.trim(),
    startDate: startDateTime,
    endDate: endDateTime,
    startDestination: formData.startDestination.trim(),
    endDestination: formData.endDestination.trim(),
    flights: flightsWithTimestamps,
    hotels: [],
    carRentals: [],
    meetings: [],
    hasCheckedBaggage: false,
    groundTransport: undefined,
    createdAt: now,
    updatedAt: now,
    source: 'manual'
  };
}

export default buildTripData;
